import type { TokenStore } from "@/domains/tokens/token-store"
import type {
  ManualClIndexerStatus,
  ManualClPoolActivityResponse,
  ManualClPoolOhlcResponse,
} from "@/domains/validators"

import type { ManualClGraphQlService } from "./manual-cl-graphql-service"
import type { ManualClPoolApiData } from "@/domains/thick-v2/types"

/**
 * Thin wrapper around `ManualClGraphQlService` used by the thick-v2
 * orchestrator. Keeps call-sites free of schema-parsing concerns.
 *
 * Filename and class name are preserved from the original `manual-cl` location
 * per the migration rule "no mechanical file renames"; the folder location is
 * what marks this as thick-v2-specific.
 */
export class ManualClGraphQlTransport {
  constructor(private graphQlService: ManualClGraphQlService) {}

  async loadManualClPools(): Promise<Array<ManualClPoolApiData>> {
    const response = await this.graphQlService.getPools()
    return response as Array<ManualClPoolApiData>
  }

  async loadPoolsByTokenPair(
    token0: TokenStore,
    token1: TokenStore,
  ): Promise<Array<ManualClPoolApiData>> {
    const response = await this.graphQlService.getRelatedTokenPools(token0, token1)
    return response as Array<ManualClPoolApiData>
  }

  async loadPoolByAddress(
    address: string,
    chainId: number,
  ): Promise<ManualClPoolApiData | undefined> {
    const response = await this.graphQlService.getPool(address, chainId)
    return response as ManualClPoolApiData | undefined
  }

  async loadPoolActivity(
    poolAddress: string,
    limit: number = 25,
    offset: number = 0,
  ): Promise<ManualClPoolActivityResponse> {
    return await this.graphQlService.getPoolActivity(poolAddress, limit, offset)
  }

  async loadPoolOhlc(poolAddress: string, sinceUnix: number): Promise<ManualClPoolOhlcResponse> {
    return await this.graphQlService.getPoolOHLC(poolAddress, sinceUnix)
  }

  async loadIndexerStatus(chainId: number = 8453): Promise<ManualClIndexerStatus | null> {
    return await this.graphQlService.getIndexerStatus(chainId)
  }
}
